/* v8 ignore next */
import type {
    GetAsyncPowerPointConversionExecutionResponse,
    GetAsyncPowerPointConversionExecutionRequest,
} from "@amzn/ads-blink-client";
import { AdsBlink, AsyncExecutionStatus } from "@amzn/ads-blink-client";
import { getCredentialsFromCacheOrCognito } from "@amzn/midway-cognito-type-script-provider";

import { TELEMETRY_ID } from "../../constants/telemetry";
import { authStore } from "../../store/auth";
import { APPENV } from "../../utils/app.environment";
import { logWithTakt } from "../../utils/telemetry.utils";

export async function getBlinkApiClientV2(): Promise<AdsBlinkIHubClient> {
    try {
        const {
            settings: { cognitoPoolId, region, blinkApiEndpoint },
        } = authStore.getState();

        if (!blinkApiEndpoint) {
            throw new Error("Blink endpoint not yet defined");
        }

        const { accessKeyId, secretAccessKey, sessionToken } =
            await getCredentialsFromCacheOrCognito({
                region,
                identityPoolId: cognitoPoolId,
                clientId: window.location.host,
                redirectUri: window.location.href,
            });

        return new AdsBlinkIHubClient({
            endpoint: `${
                APPENV.mock_server ? "http" : "https"
            }://${blinkApiEndpoint}`,
            region,
            credentials: { accessKeyId, secretAccessKey, sessionToken },
        });
    } catch (e: any) {
        throw new Error(e.message);
    }
}

export class AdsBlinkIHubClient extends AdsBlink {
    pollGetAsyncOfficeConvert: (
        input: GetAsyncPowerPointConversionExecutionRequest,
    ) => Promise<GetAsyncPowerPointConversionExecutionResponse> = async (
        input,
    ) => {
        const POLL_RATE = 500; // ms
        const MAX_POLL_COUNT = 1800; // 15 minutes at poll rate of 500
        const MAX_ERRORS = 5; // Maximum number of errors allowed during polling

        let pollIndex = 0;
        let errorCount = 0;

        let output: GetAsyncPowerPointConversionExecutionResponse | null = null;
        let asyncConversionError = false;

        do {
            pollIndex++;
            try {
                const response = await this.getAsyncOfficeConvert({
                    executionId: input.executionId,
                });
                if (response.status === AsyncExecutionStatus.SUCCESS) {
                    output = response;

                    // emit telemetry if preview duration is long
                    if (pollIndex > 240)
                        // over two minutes
                        logWithTakt(
                            TELEMETRY_ID.ASYNC_OFFICE_CONVERT_DURATION_OVER_2_MIN,
                            { execution: input.executionId },
                        );
                    else if (pollIndex > 120)
                        // over one minutes
                        logWithTakt(
                            TELEMETRY_ID.ASYNC_OFFICE_CONVERT_DURATION_OVER_1_MIN,
                            { execution: input.executionId },
                        );
                } else if (response.status === AsyncExecutionStatus.ERROR) {
                    asyncConversionError = true;
                    output = response;
                }
            } catch (err) {
                errorCount++;
            }
            if (output === null) {
                await new Promise((resolve) => setTimeout(resolve, POLL_RATE));
            }
        } while (
            !asyncConversionError &&
            output === null &&
            pollIndex < MAX_POLL_COUNT &&
            errorCount < MAX_ERRORS
        );

        if (output?.status === AsyncExecutionStatus.ERROR) {
            throw new Error("Async Office Convert failed");
        }

        if (output === null) {
            throw new Error("Polling timed out");
        } else {
            return output;
        }
    };
}
